/**
 * The visual arena, built from the COLLISION triangles.
 *
 * `soccarTriangles()` is the same Float64Array the physics collides against, so
 * the thing you see and the thing you bounce off cannot drift apart: there is
 * no second arena model to keep in sync, and a change to the collision mesh
 * shows up on screen immediately. The only things added on top are decoration
 * that has no collision anyway — goal frames, nets, boost pads, light rigs.
 *
 * Triangles are sorted into four surface kinds by their face normal, because a
 * single material over the whole shell throws away the read. The floor wants a
 * bright pitch texture, the walls want to be nearly transparent so the stadium
 * stays legible from behind a goal, the ramps want to be opaque so the field
 * edge is obvious at speed, and the ceiling wants to be almost invisible.
 *
 * Nothing in this file touches a canvas or a WebGL context: geometry is built
 * from arithmetic and the decorative materials are texture-free, so the whole
 * module can be imported and exercised in Node.
 */

import {
  BufferAttribute,
  BufferGeometry,
  CatmullRomCurve3,
  CircleGeometry,
  Color,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  TubeGeometry,
  Vector3,
} from 'three';
import { BOOST_PADS, GOAL } from '../physics/arena-data';
import { ARENA } from '../physics/constants';
import { soccarTriangles } from '../physics/arena';
import { toThree } from './convert';
import { PALETTE, TEAM_COLORS, fieldUv, goalYForTeam, teamForY } from './materials';

// ---------------------------------------------------------------------------
// Documented extents
// ---------------------------------------------------------------------------

/**
 * What the built mesh is allowed to span, in the PHYSICS frame. The shell is
 * bounded by the arena planes; the goal tubes are the only thing that reaches
 * past the back walls, out to the back of the net.
 */
export const ARENA_EXTENTS = {
  minX: -ARENA.halfWidth,
  maxX: ARENA.halfWidth,
  /** Shell only — the goal interiors go further. */
  minShellY: -ARENA.halfLength,
  maxShellY: ARENA.halfLength,
  minY: -ARENA.backNetY,
  maxY: ARENA.backNetY,
  minZ: ARENA.floor,
  maxZ: ARENA.ceiling,
} as const;

// ---------------------------------------------------------------------------
// Surface classification
// ---------------------------------------------------------------------------

export type SurfaceKind =
  | 'floor'
  | 'ramp'
  | 'wall'
  | 'ceiling'
  | 'goal-blue'
  | 'goal-orange';

export const SURFACE_KINDS: readonly SurfaceKind[] = [
  'floor',
  'ramp',
  'wall',
  'ceiling',
  'goal-blue',
  'goal-orange',
];

/** Surface kind of a team's goal interior. Blue is -Y; see ./materials. */
export const goalSurfaceKind = (team: 0 | 1): SurfaceKind =>
  team === 0 ? 'goal-blue' : 'goal-orange';

/** Triangle vertices are 9 consecutive floats; this is one of them. */
export interface TriangleView {
  ax: number; ay: number; az: number;
  bx: number; by: number; bz: number;
  cx: number; cy: number; cz: number;
}

export const readTriangle = (triangles: Float64Array, index: number): TriangleView => {
  const o = index * 9;
  return {
    ax: triangles[o], ay: triangles[o + 1], az: triangles[o + 2],
    bx: triangles[o + 3], by: triangles[o + 4], bz: triangles[o + 5],
    cx: triangles[o + 6], cy: triangles[o + 7], cz: triangles[o + 8],
  };
};

/** Unit face normal in the physics frame, wound the same way `MeshArena` does. */
export const faceNormal = (t: TriangleView): [number, number, number] => {
  const e1x = t.bx - t.ax, e1y = t.by - t.ay, e1z = t.bz - t.az;
  const e3x = t.ax - t.cx, e3y = t.ay - t.cy, e3z = t.az - t.cz;
  const nx = e3y * e1z - e3z * e1y;
  const ny = e3z * e1x - e3x * e1z;
  const nz = e3x * e1y - e3y * e1x;
  const len = Math.hypot(nx, ny, nz);
  return len === 0 ? [0, 0, 0] : [nx / len, ny / len, nz / len];
};

/**
 * Sort a triangle into a surface kind.
 *
 * Order matters. The goal test comes first because a goal-tube triangle can
 * have any normal at all, including a perfect floor normal (the goal floor) —
 * classifying by normal first would scatter half of each goal into the pitch.
 */
export const classifyTriangle = (t: TriangleView): SurfaceKind => {
  const maxAbsY = Math.max(Math.abs(t.ay), Math.abs(t.by), Math.abs(t.cy));
  if (maxAbsY > ARENA.halfLength + 1e-6) {
    return goalSurfaceKind(teamForY((t.ay + t.by + t.cy) / 3));
  }

  const [, , nz] = faceNormal(t);
  const cz = (t.az + t.bz + t.cz) / 3;

  // The flat pitch: normal exactly up and sitting on z = 0.
  if (nz > 0.999 && cz <= 1) return 'floor';
  // Anything else still facing upward is a floor fillet, i.e. a ramp.
  if (nz > 0.05) return 'ramp';
  if (nz < -0.5) return 'ceiling';
  return 'wall';
};

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/**
 * Weld threshold for normal smoothing, in uu. The collision mesh stores every
 * triangle with its own three vertices, so shared corners have to be found by
 * position. 1e-3 is far below the smallest feature (the 96 uu goal fillet) and
 * far above float noise from the arc arithmetic.
 */
const WELD_EPSILON = 1e-3;

/**
 * Face normals within this angle of each other are averaged; beyond it the
 * edge stays hard. Without the cutoff the floor-to-wall seam smears into a
 * gradient and the arena loses every edge it has. cos(50 degrees).
 */
const SMOOTH_COS = Math.cos((50 * Math.PI) / 180);

/**
 * Per-vertex normals with hard edges preserved. Returns THREE-frame normals for
 * a THREE-frame position buffer, one normal per vertex, no index buffer.
 */
export const smoothNormals = (positions: Float32Array): Float32Array => {
  const vertexCount = positions.length / 3;
  const faceCount = vertexCount / 3;

  const faceNormals = new Float32Array(faceCount * 3);
  for (let f = 0; f < faceCount; f++) {
    const o = f * 9;
    const ax = positions[o], ay = positions[o + 1], az = positions[o + 2];
    const bx = positions[o + 3], by = positions[o + 4], bz = positions[o + 5];
    const cx = positions[o + 6], cy = positions[o + 7], cz = positions[o + 8];
    const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
    const e2x = cx - ax, e2y = cy - ay, e2z = cz - az;
    let nx = e1y * e2z - e1z * e2y;
    let ny = e1z * e2x - e1x * e2z;
    let nz = e1x * e2y - e1y * e2x;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    faceNormals[f * 3] = nx;
    faceNormals[f * 3 + 1] = ny;
    faceNormals[f * 3 + 2] = nz;
  }

  // Bucket faces by welded vertex position.
  const buckets = new Map<string, number[]>();
  const inv = 1 / WELD_EPSILON;
  const keyAt = (v: number): string => {
    const o = v * 3;
    return `${Math.round(positions[o] * inv)},${Math.round(positions[o + 1] * inv)},${Math.round(
      positions[o + 2] * inv
    )}`;
  };
  for (let v = 0; v < vertexCount; v++) {
    const key = keyAt(v);
    const list = buckets.get(key);
    if (list) list.push(v);
    else buckets.set(key, [v]);
  }

  const out = new Float32Array(vertexCount * 3);
  for (let v = 0; v < vertexCount; v++) {
    const own = (v / 3) | 0;
    const ox = faceNormals[own * 3];
    const oy = faceNormals[own * 3 + 1];
    const oz = faceNormals[own * 3 + 2];
    let sx = 0, sy = 0, sz = 0;
    for (const other of buckets.get(keyAt(v)) ?? [v]) {
      const f = (other / 3) | 0;
      const fx = faceNormals[f * 3];
      const fy = faceNormals[f * 3 + 1];
      const fz = faceNormals[f * 3 + 2];
      if (fx * ox + fy * oy + fz * oz >= SMOOTH_COS) {
        sx += fx; sy += fy; sz += fz;
      }
    }
    const len = Math.hypot(sx, sy, sz);
    if (len > 1e-9) {
      out[v * 3] = sx / len;
      out[v * 3 + 1] = sy / len;
      out[v * 3 + 2] = sz / len;
    } else {
      out[v * 3] = ox; out[v * 3 + 1] = oy; out[v * 3 + 2] = oz;
    }
  }
  return out;
};

export interface SurfaceBuffers {
  kind: SurfaceKind;
  /** THREE-frame vertex positions, 9 floats per triangle. */
  positions: Float32Array;
  uvs: Float32Array;
  triangleCount: number;
}

/**
 * Wall/ceiling UV: cylindrical, so the panelling wraps the shell continuously
 * instead of stretching across the corner planes. The wall texture repeats, so
 * the seam at atan2's branch cut is one texel wide and invisible.
 */
const shellUv = (x: number, y: number, z: number): [number, number] => [
  Math.atan2(y, x) / (Math.PI * 2) + 0.5,
  z / ARENA.ceiling,
];

/**
 * Split the collision triangles into per-surface THREE-frame buffers.
 * Pure arithmetic — safe in Node, and the basis of the geometry tests.
 */
export const buildArenaSurfaces = (
  triangles: Float64Array = soccarTriangles()
): SurfaceBuffers[] => {
  const count = triangles.length / 9;
  const byKind = new Map<SurfaceKind, number[]>();
  for (const k of SURFACE_KINDS) byKind.set(k, []);
  for (let i = 0; i < count; i++) {
    byKind.get(classifyTriangle(readTriangle(triangles, i)))!.push(i);
  }

  const scratch = new Vector3();
  const out: SurfaceBuffers[] = [];
  for (const kind of SURFACE_KINDS) {
    const indices = byKind.get(kind)!;
    const positions = new Float32Array(indices.length * 9);
    const uvs = new Float32Array(indices.length * 6);
    for (let n = 0; n < indices.length; n++) {
      const o = indices[n] * 9;
      for (let v = 0; v < 3; v++) {
        const x = triangles[o + v * 3];
        const y = triangles[o + v * 3 + 1];
        const z = triangles[o + v * 3 + 2];
        // The one and only frame conversion — see ./convert.
        toThree({ x, y, z }, scratch);
        const p = n * 9 + v * 3;
        positions[p] = scratch.x;
        positions[p + 1] = scratch.y;
        positions[p + 2] = scratch.z;
        const [u, vv] =
          kind === 'floor' || kind === 'ramp' ? fieldUv(x, y) : shellUv(x, y, z);

        uvs[n * 6 + v * 2] = u;
        uvs[n * 6 + v * 2 + 1] = vv;
      }
    }
    out.push({ kind, positions, uvs, triangleCount: indices.length });
  }
  return out;
};

export const surfaceGeometry = (buffers: SurfaceBuffers): BufferGeometry => {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(buffers.positions, 3));
  geometry.setAttribute('normal', new BufferAttribute(smoothNormals(buffers.positions), 3));
  geometry.setAttribute('uv', new BufferAttribute(buffers.uvs, 2));
  geometry.computeBoundingSphere();
  return geometry;
};

/** Axis-aligned bounds of a THREE-frame position buffer. */
export const bufferBounds = (
  positions: Float32Array
): { min: [number, number, number]; max: [number, number, number] } => {
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < positions.length; i += 3) {
    for (let a = 0; a < 3; a++) {
      const v = positions[i + a];
      if (v < min[a]) min[a] = v;
      if (v > max[a]) max[a] = v;
    }
  }
  return { min, max };
};

// ---------------------------------------------------------------------------
// Goals
// ---------------------------------------------------------------------------

/** Net lattice spacing, uu. Free parameter — nothing collides with the net.
 *  Too fine and the lattice aliases into a solid slab at goal-to-goal range. */
const NET_CELL = 110;
/** Radius of the goal frame tube, uu. */
const GOAL_FRAME_RADIUS = 16;

export interface GoalVisual {
  team: 0 | 1;
  /** Mouth plane Y in the PHYSICS frame. Negative for blue by definition. */
  y: number;
  color: number;
  group: Group;
  frame: Mesh;
  net: LineSegments;
}

/**
 * Outline of the goal mouth: the same rounded rectangle the collision mesh
 * uses, sampled as a closed curve so the frame tube follows it exactly.
 */
export const goalMouthOutline = (segments = 8): [number, number][] => {
  const hw = GOAL.centerToPostMesh;
  const h = GOAL.heightMesh;
  const r = GOAL.cornerFilletRadius;
  const points: [number, number][] = [];
  const arc = (cx: number, cz: number, a0: number, a1: number): void => {
    for (let i = 0; i <= segments; i++) {
      const a = a0 + ((a1 - a0) * i) / segments;
      points.push([cx + r * Math.cos(a), cz + r * Math.sin(a)]);
    }
  };
  arc(hw - r, r, -Math.PI / 2, 0);
  arc(hw - r, h - r, 0, Math.PI / 2);
  arc(-(hw - r), h - r, Math.PI / 2, Math.PI);
  arc(-(hw - r), r, Math.PI, Math.PI * 1.5);
  return points;
};

/**
 * The net: a lattice on the back, sides and roof of the goal box, drawn as
 * lines rather than a solid so you can see the ball inside it. A filled box
 * here would hide the one thing the player most wants to see.
 */
const buildNetGeometry = (team: 0 | 1): BufferGeometry => {
  const hw = GOAL.centerToPostMesh;
  const h = GOAL.heightMesh;
  const y0 = goalYForTeam(team);
  const y1 = team === 0 ? -ARENA.backNetY : ARENA.backNetY;
  const verts: number[] = [];
  const scratch = new Vector3();
  const push = (x: number, y: number, z: number): void => {
    toThree({ x, y, z }, scratch);
    verts.push(scratch.x, scratch.y, scratch.z);
  };
  const line = (
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number
  ): void => {
    push(ax, ay, az);
    push(bx, by, bz);
  };

  const steps = (span: number): number => Math.max(2, Math.round(Math.abs(span) / NET_CELL));
  const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

  const nx = steps(hw * 2);
  const nz = steps(h);
  const ny = steps(y1 - y0);

  // Back panel.
  for (let i = 0; i <= nx; i++) {
    const x = lerp(-hw, hw, i / nx);
    line(x, y1, 0, x, y1, h);
  }
  for (let k = 0; k <= nz; k++) {
    const z = lerp(0, h, k / nz);
    line(-hw, y1, z, hw, y1, z);
  }
  // Side panels and roof.
  for (const sx of [-1, 1]) {
    for (let j = 0; j <= ny; j++) {
      const y = lerp(y0, y1, j / ny);
      line(sx * hw, y, 0, sx * hw, y, h);
    }
    for (let k = 0; k <= nz; k++) {
      const z = lerp(0, h, k / nz);
      line(sx * hw, y0, z, sx * hw, y1, z);
    }
  }
  for (let i = 0; i <= nx; i++) {
    const x = lerp(-hw, hw, i / nx);
    line(x, y0, h, x, y1, h);
  }
  for (let j = 0; j <= ny; j++) {
    const y = lerp(y0, y1, j / ny);
    line(-hw, y, h, hw, y, h);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(verts), 3));
  return geometry;
};

/**
 * Both goals, team-coloured. Blue is at -Y and orange at +Y — enforced through
 * `goalYForTeam`/`teamForY` in ./materials rather than by writing the sign
 * twice, and asserted in the tests.
 */
export const buildGoals = (): GoalVisual[] =>
  ([0, 1] as const).map((team) => {
    const y = goalYForTeam(team);
    const color = TEAM_COLORS[team];

    const outline = goalMouthOutline();
    const scratch = new Vector3();
    const curve = new CatmullRomCurve3(
      outline.map(([x, z]) => toThree({ x, y, z }, new Vector3()).clone()),
      true,
      'catmullrom',
      0
    );
    const frame = new Mesh(
      new TubeGeometry(curve, outline.length * 2, GOAL_FRAME_RADIUS, 8, true),
      new MeshStandardMaterial({
        color: PALETTE.chrome,
        emissive: new Color(color),
        emissiveIntensity: 0.5,
        metalness: 0.9,
        roughness: 0.25,
      })
    );
    frame.castShadow = false;

    const net = new LineSegments(
      buildNetGeometry(team),
      new LineBasicMaterial({ color, transparent: true, opacity: 0.5 })
    );

    // A soft emissive disc on the goal line, so the mouth glows in team colour.
    const glow = new Mesh(
      new CircleGeometry(GOAL.centerToPostMesh * 1.15, 48),
      new MeshBasicMaterial({ color, transparent: true, opacity: 0.12, depthWrite: false })
    );
    toThree({ x: 0, y, z: GOAL.heightMesh / 2 }, scratch);
    glow.position.copy(scratch);
    glow.rotation.y = team === 0 ? 0 : Math.PI;

    const group = new Group();
    group.name = `goal-${team === 0 ? 'blue' : 'orange'}`;
    group.add(frame, net, glow);

    return { team, y, color, group, frame, net };
  });

// ---------------------------------------------------------------------------
// Boost pads
// ---------------------------------------------------------------------------

export interface BoostPadVisuals {
  group: Group;
  /** Set a pad's availability; picked-up pads dim and shrink rather than vanish. */
  setActive(index: number, active: boolean): void;
  /** Animate the idle pulse. `time` is seconds; deterministic in `time`. */
  update(time: number): void;
}

const BOOST_PAD_COLOR = 0xffc23a;

export const buildBoostPads = (): BoostPadVisuals => {
  const group = new Group();
  group.name = 'boost-pads';

  const pads: { mesh: Mesh; ring: Mesh; big: boolean; active: boolean; phase: number }[] = [];
  const scratch = new Vector3();

  BOOST_PADS.forEach((pad, i) => {
    const radius = pad.isBig ? 160 : 70;
    const discMaterial = new MeshBasicMaterial({
      color: BOOST_PAD_COLOR,
      transparent: true,
      opacity: pad.isBig ? 0.45 : 0.3,
      depthWrite: false,
    });
    const disc = new Mesh(new CircleGeometry(radius, pad.isBig ? 40 : 20), discMaterial);
    // Lie flat on the pitch, a hair above it so it never z-fights.
    disc.rotation.x = -Math.PI / 2;
    toThree({ x: pad.position.x, y: pad.position.y, z: 4 }, scratch);
    disc.position.copy(scratch);

    const ringMaterial = new MeshBasicMaterial({
      color: BOOST_PAD_COLOR,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
    });
    const ring = new Mesh(new CircleGeometry(radius * 0.24, 16), ringMaterial);
    ring.rotation.x = -Math.PI / 2;
    toThree({ x: pad.position.x, y: pad.position.y, z: pad.isBig ? 40 : 18 }, scratch);
    ring.position.copy(scratch);

    group.add(disc, ring);
    // The phase offset is a pure function of the pad index, so the field
    // breathes out of sync without any randomness.
    pads.push({ mesh: disc, ring, big: pad.isBig, active: true, phase: (i * 0.37) % 1 });
  });

  return {
    group,
    setActive(index: number, active: boolean): void {
      const pad = pads[index];
      if (pad) pad.active = active;
    },
    update(time: number): void {
      for (const pad of pads) {
        const pulse = 0.5 + 0.5 * Math.sin((time * 1.6 + pad.phase * Math.PI * 2) % (Math.PI * 2));
        const base = pad.big ? 0.45 : 0.28;
        const target = pad.active ? base + pulse * 0.25 : 0.05;
        const m = pad.mesh.material as MeshBasicMaterial;
        m.opacity = target;
        const rm = pad.ring.material as MeshBasicMaterial;
        rm.opacity = pad.active ? 0.75 + pulse * 0.25 : 0.05;
        const s = pad.active ? 1 : 0.6;
        pad.ring.scale.setScalar(s * (0.9 + pulse * 0.2));
      }
    },
  };
};

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

export interface ArenaMeshMaterials {
  floor: MeshStandardMaterial;
  ramp?: MeshStandardMaterial;
  wall: MeshStandardMaterial;
  ceiling: MeshStandardMaterial;
  goalInterior: [MeshStandardMaterial, MeshStandardMaterial];
}

export interface ArenaMesh {
  group: Group;
  goals: GoalVisual[];
  boostPads: BoostPadVisuals;
  /** Per-kind meshes, for debugging and for the tests. */
  surfaces: Map<SurfaceKind, Mesh>;
}

/**
 * Assemble the arena. `materials` is passed in rather than built here so this
 * module never needs a canvas: the caller supplies whatever the scene has.
 */
export const buildArena = (materials: ArenaMeshMaterials): ArenaMesh => {
  const group = new Group();
  group.name = 'arena';

  const surfaces = new Map<SurfaceKind, Mesh>();
  const rampMaterial =
    materials.ramp ??
    new MeshStandardMaterial({ color: PALETTE.wall, roughness: 0.4, metalness: 0.6 });

  for (const buffers of buildArenaSurfaces()) {
    if (buffers.triangleCount === 0) continue;
    const geometry = surfaceGeometry(buffers);
    let material: MeshStandardMaterial;
    switch (buffers.kind) {
      case 'floor': material = materials.floor; break;
      case 'ramp': material = rampMaterial; break;
      case 'wall': material = materials.wall; break;
      case 'ceiling': material = materials.ceiling; break;
      case 'goal-blue': material = materials.goalInterior[0]; break;
      case 'goal-orange': material = materials.goalInterior[1]; break;
    }
    const mesh = new Mesh(geometry, material);
    mesh.name = `arena-${buffers.kind}`;
    // Only the pitch and ramps receive; the shell is transparent and would
    // shadow-acne against itself.
    mesh.receiveShadow = buffers.kind === 'floor' || buffers.kind === 'ramp';
    mesh.renderOrder = buffers.kind === 'wall' || buffers.kind === 'ceiling' ? 1 : 0;
    surfaces.set(buffers.kind, mesh);
    group.add(mesh);
  }

  const goals = buildGoals();
  for (const goal of goals) group.add(goal.group);

  const boostPads = buildBoostPads();
  group.add(boostPads.group);

  return { group, goals, boostPads, surfaces };
};

/** Which team's half a physics position is in. Re-exported for the HUD. */
export const teamHalf = (y: number): 0 | 1 => teamForY(y);

/** Every object in the arena group, for disposal. */
export const disposeArena = (arena: ArenaMesh): void => {
  arena.group.traverse((o: Object3D) => {
    const mesh = o as Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
  });
};
